import { Column, Tooltip } from '@acceso/design-system';
import { useTranslation } from 'react-i18next';

import { RenderActionButtons } from '@/components/RenderActionButtons';
import { TableRow } from '@/components/TableRow';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { TClipDeclaration } from '@/features/clips/types';
import { getOffsetHHMMSS } from '@/utils/hh-mm-ss-into-number';

type ClipToolkitSubTableProps = {
  item: TClipDeclaration;
  columns: Column<TClipDeclaration>[];
  onPreview?: (item: TClipDeclaration) => void;
  onUpdate?: (item: TClipDeclaration) => void;
  onDelete?: (item: TClipDeclaration) => void;
};

export function ClipToolkitDeclarationsSubTable({
  item,
  columns,
  onPreview,
  onUpdate,
  onDelete,
}: ClipToolkitSubTableProps) {
  const { activeClip } = useBroadcastScheduleProgram();
  const { t } = useTranslation();

  if (!activeClip) return null;
  return (
    <td colSpan={columns.length} className="p-0">
      <table className="w-full border-separate border-spacing-0">
        <colgroup>
          {columns.map((column, index) => (
            <col
              key={index}
              style={{
                width: column.width || `${100 / columns.length}%`,
                maxWidth: column.width || `${100 / columns.length}%`,
                minWidth: column.minWidth || 'auto',
              }}
            />
          ))}
        </colgroup>
        <tbody>
          {item.children &&
            item.children.length > 0 &&
            item.children.map(child => {
              const seconds = getOffsetHHMMSS(child.start_datetime, activeClip.start_datetime);

              return (
                <tr
                  key={child.id}
                  className="group border-b border-neutral-30 bg-neutral-15 last:border-0 hover:bg-neutral-10"
                >
                  <td className="table-cell gap-size-12 p-size-8 pr-size-20"></td>
                  <Tooltip help={child.text || ''}>
                    <TableRow content={child.text || ''} />
                  </Tooltip>
                  <td className="table-cell gap-size-12 p-size-8 pr-size-20">
                    <TableRow content={seconds} />
                  </td>

                  <td>
                    {child.review_state === 1 ? (
                      <TableRow
                        className="!font-semibold !text-feedback-success-30"
                        content={t('Si')}
                      />
                    ) : (
                      <TableRow
                        className="!font-semibold !text-feedback-warning-30"
                        content={t('No')}
                      />
                    )}
                  </td>

                  <td className="table-cell gap-size-12 p-size-8 pr-size-20">
                    <RenderActionButtons
                      item={child}
                      onPreview={() => onPreview?.(child)}
                      onUpdate={() => onUpdate?.(child)}
                      onDelete={() => onDelete?.(child)}
                    />
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </td>
  );
}
